import {
  alphabeticalCompare,
  DSStatus,
  alphabeticalSort,
  SharePointParentFolder,
} from '@cims-online/shared'
import { Disciplines } from './components/Disciplines'
import { Actions } from './components/Actions'
import { Status } from './components/Status'
import { CIMSColumnsOptions } from 'components/CIMSTable/CIMSColumn/types'
import { useDesignSubmittalLogContext } from '../../../../DesignSubmittalLogContext'
import { getDeepestString } from 'utils/getReactNodeLength'
import { HelpMessage } from 'utils/constants'
import { useSingleUserColumn } from 'components/CIMSTable/common/hooks/useSingleUserColumn'
import { dateFormatter } from 'utils/dateFormatter'
import { DesignSubmittal } from '../../../../DesignSubmittalLogContext/types'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'

const help = HelpMessage.DesignSubmittals.Log

export const useColumns = (): CIMSColumnsOptions<DesignSubmittal> => {
  const { refetch, edit, selectOptions } = useDesignSubmittalLogContext()
  return {
    prominentCustomFields: {
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === DSStatus.ReturnedToDesignBuilder.id
            ? {
                why: 'invalid status',
                statusName: DSStatus.ReturnedToDesignBuilder.name,
              }
            : false,
        recordInactive: deleted,
      }),
      onCheckBoolean: async ({ id }, input) => {
        await edit({ id, customFields: { booleanInputs: [input] } })
        refetch()
      },
      onCheckBooleans: async ({ id }, input) => {
        await edit({
          id,
          customFields: { listInputs: { checkboxes: [input] } },
        })
        refetch()
      },
    },
    columns: [
      {
        title: 'Status',
        help: help.Status,
        render: (record) => <Status {...record} />,
        width: 140,
        filter: {
          with: 'items',
          key: 'statusId',
          items: DSStatus.values
            .sort(alphabeticalSort('priority'))
            .map(({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })),
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) => {
            const A = DSStatus.fromIdOrFail(a.statusId)
            const B = DSStatus.fromIdOrFail(b.statusId)
            return alphabeticalCompare(A, B, 'priority')
          },
          default: 'ascend',
          priority: 1,
        },
      },
      useExternalLinks<DesignSubmittal>({
        type: 'sharepoint',
        dataFn: ({
          id,
          externalLinks,
          sharePointResource,
          sharePointFolderName,
        }) => ({
          recordId: id,
          externalLinks,
          folder: sharePointFolderName,
          resource: { webUrl: sharePointResource?.webUrl },
        }),
        parent: SharePointParentFolder.DesignSubmittals,
      }),
      {
        title: 'Package Number',
        help: help.PackageNumber,
        render: ({ packageNumber }) => packageNumber,
        width: 225,
        sort: { with: 'key', key: 'packageNumber' },
        filter: { type: 'search', with: 'key', key: 'packageNumber' },
      },
      {
        title: 'Package Name',
        help: help.PackageName,
        render: ({ packageName }) => packageName,
        width: 210,
        filter: { type: 'search', with: 'key', key: 'packageName' },
        sort: { with: 'key', key: 'packageName' },
      },
      {
        title: 'Type',
        help: help.Type,
        render: ({ type }) => type.name,
        width: 125,
        filter: {
          with: 'callback',
          items: selectOptions.typesOptions
            .filter(({ value }) => value !== undefined && value !== null)
            .map(({ label, value }) => ({
              value: value!,
              text: getDeepestString(label),
            })),
          onFilter: (value, { type }) => type.id === value,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) => alphabeticalCompare(a.type, b.type, 'name'),
        },
      },
      {
        title: 'Disciplines',
        help: help.Disciplines,
        render: (record) => <Disciplines {...record} />,
        width: 175,
        filter: {
          with: 'callback',
          items: selectOptions.disciplinesOptions
            .filter(({ value }) => value !== null && value !== undefined)
            .map(({ label, value }) => ({
              value: value!,
              text: getDeepestString(label),
            })),
          onFilter: (value, { dsToDSDisciplines }) =>
            dsToDSDisciplines.some(({ discipline: { id } }) => value === id),
        },
      },
      {
        title: 'Actions',
        help: help.Actions,
        render: (record) => <Actions {...record} />,
        filter: {
          with: 'callback',
          items: selectOptions.actionsOptions
            .filter(({ value }) => value !== null && value !== undefined)
            .map(({ label, value }) => ({
              value: value!,
              text: getDeepestString(label),
            })),
          onFilter: (value, { actions }) =>
            actions.some(({ id }) => value === id),
        },
        width: 175,
      },
      {
        title: 'Requested Action Date',
        help: help.RequestedActionDate,
        render: ({ requestedActionDate }) =>
          dateFormatter.simple(requestedActionDate),
        width: 235,
        filter: { type: 'date', with: 'key', key: 'requestedActionDate' },
        sort: { with: 'key', key: 'requestedActionDate' },
      },
      {
        title: 'Contract Due Date',
        help: help.ContractDueDate,
        render: ({ contractDueDate }) => dateFormatter.simple(contractDueDate),
        width: 205,
        filter: { type: 'date', with: 'key', key: 'contractDueDate' },
        sort: { with: 'key', key: 'contractDueDate' },
      },
      {
        title: 'Disposition',
        help: help.Disposition,
        render: ({ disposition }) => disposition,
        width: 225,
        filter: {
          with: 'key',
          key: 'disposition',
        },
        sort: { with: 'key', key: 'disposition' },
      },
      {
        title: 'Disposition Date',
        help: help.DispositionDate,
        render: ({ dispositionDate }) => dateFormatter.simple(dispositionDate),
        width: 190,
        filter: { type: 'date', with: 'key', key: 'dispositionDate' },
        sort: { with: 'key', key: 'dispositionDate' },
      },
      useSingleUserColumn({
        title: 'Disposition By',
        help: help.DispositionBy,
        user: ({ dispositionBy }) => dispositionBy,
      }),
    ],
  }
}
